import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("token"); // PayPal returns the order id in ?token=
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const { user } = useAuth();

  const pending = (() => {
    try { return JSON.parse(sessionStorage.getItem("dpa_pending_checkout") || "null"); } catch { return null; }
  })();

  useEffect(() => {
    if (!orderId) {
      setState({ status: "error", error: "Missing order token", data: null });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post(`/paypal/orders/${orderId}/capture`);
        setState({ status: "success", data, error: null });
        sessionStorage.removeItem("dpa_pending_checkout");
      } catch (e) {
        setState({ status: "error", data: null, error: e?.response?.data?.detail || e.message || "Capture failed" });
      }
    })();
  }, [orderId]);

  const label = pending?.label || state.data?.name || "your purchase";

  return (
    <div className="container-narrow py-16 md:py-24" data-testid="checkout-success-page">
      <div className="max-w-xl mx-auto card-soft p-8 md:p-10 text-center">
        {state.status === "loading" && (
          <>
            <Loader2 className="w-12 h-12 text-marigold animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-2xl md:text-3xl font-semibold mb-2">Finalising your payment…</h1>
            <p className="text-ink2 text-sm">Hold tight, we&apos;re confirming with PayPal.</p>
          </>
        )}

        {state.status === "success" && (
          <>
            <div className="w-16 h-16 rounded-full bg-teal/15 text-teal flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <div className="label-eyebrow mb-2 inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Payment successful</div>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold mb-2">You&apos;re all set!</h1>
            <p className="text-ink2 mb-6">Thanks for buying <span className="font-semibold text-ink">{label}</span>. Everything is now active on your account.</p>

            {state.data?.fulfillment?.tier && (
              <div className="bg-paper rounded-xl border border-border px-4 py-3 mb-6 text-left text-sm" data-testid="fulfillment-tier">
                <div className="text-ink2 uppercase tracking-widest text-[10px] font-semibold mb-1">Tier activated</div>
                <div className="font-heading font-semibold capitalize">{state.data.fulfillment.tier}</div>
                {state.data.fulfillment.expires_at && (
                  <div className="text-xs text-ink2 mt-0.5">Access through {new Date(state.data.fulfillment.expires_at).toLocaleDateString()}</div>
                )}
              </div>
            )}

            {(state.data?.fulfillment?.credits_added || state.data?.fulfillment?.bonus_credits) ? (
              <div className="bg-paper rounded-xl border border-border px-4 py-3 mb-6 text-left text-sm" data-testid="fulfillment-credits">
                <div className="text-ink2 uppercase tracking-widest text-[10px] font-semibold mb-1">Credits added</div>
                <div className="font-heading font-semibold">+{state.data.fulfillment.credits_added || state.data.fulfillment.bonus_credits} prompts</div>
              </div>
            ) : null}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={user ? "/dashboard" : "/"} className="btn-primary" data-testid="go-dashboard-btn">
                Go to dashboard <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link to="/studio" className="btn-outline-ink text-sm" data-testid="go-studio-btn">
                Start building
              </Link>
            </div>
          </>
        )}

        {state.status === "error" && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-5">
              <XCircle className="w-9 h-9" />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-semibold mb-2">We couldn&apos;t confirm the payment</h1>
            <p className="text-ink2 mb-6 text-sm">{state.error}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/pricing" className="btn-primary" data-testid="retry-checkout-btn">
                Back to pricing <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <a href="mailto:hello@durgaprogrammingadventures.com" className="btn-outline-ink text-sm">
                Contact support
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
